import React, { useEffect, useState, useCallback, useMemo } from 'react';
import SeasonEventPerformanceTable from './SeasonEventPerformanceTable/SeasonEventPerformanceTable';
import PropTypes from 'prop-types';
import LoadingContent from 'components/NewDesign/LoadingContent';
import { toast } from 'react-toastify';
import { getSeasonEventPerformance } from 'api/reportsApi';

const SeasonEventPerformance = ({ clubID, onDataChange }) => {
  const storageKey = `seasonEventPerformanceFilters_${clubID}`;

  const getSavedFilters = () => {
    try {
      const saved = sessionStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };

  const savedFilters = getSavedFilters();

  const [season, setSeason] = useState(null);
  const [athletes, setAthletes] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedAge, setSelectedAge] = useState(savedFilters?.selectedAge || '');
  const [selectedGender, setSelectedGender] = useState(savedFilters?.selectedGender || '');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const filters = { selectedAge, selectedGender };
    sessionStorage.setItem(storageKey, JSON.stringify(filters));
  }, [selectedAge, selectedGender, storageKey]);

  const fetchData = useCallback(async () => {
    try {
      if (clubID) {
        setLoading(true);
        const response = await getSeasonEventPerformance(clubID);
        const data = response.data || {};
        setSeason(data.season || null);
        setAthletes(data.athletes || []);
      }
      setLoading(false);
    } catch (err) {
      console.error('[SeasonEventPerformance] Fetch error: ', err);
      toast.error('Failed to load season event performance data');
      setLoading(false);
    }
  }, [clubID]);

  useEffect(() => {
    if (clubID) {
      fetchData();
    }
  }, [fetchData, clubID]);

  const availableAges = useMemo(() => {
    const ages = [...new Set(athletes.map((a) => a.age))];
    return ages.sort((a, b) => a - b);
  }, [athletes]);

  const filteredAthletes = useMemo(() => {
    let results = athletes;

    if (searchText) {
      const search = searchText.toLowerCase();
      results = results.filter((a) => {
        const fullName = `${a.firstName} ${a.lastName}`.toLowerCase();
        return fullName.includes(search);
      });
    }

    if (selectedAge) {
      results = results.filter((a) => a.age === Number(selectedAge));
    }

    if (selectedGender) {
      results = results.filter((a) => a.gender === selectedGender);
    }

    return results;
  }, [athletes, searchText, selectedAge, selectedGender]);

  useEffect(() => {
    if (onDataChange) {
      onDataChange({
        hasData: filteredAthletes.length > 0,
        seasonName: season?.seasonName || '',
      });
    }
  }, [filteredAthletes, season, onDataChange]);

  return (
    <LoadingContent loading={loading}>
      <SeasonEventPerformanceTable
        athletes={filteredAthletes}
        searchText={searchText}
        setSearchText={setSearchText}
        selectedAge={selectedAge}
        setSelectedAge={setSelectedAge}
        selectedGender={selectedGender}
        setSelectedGender={setSelectedGender}
        availableAges={availableAges}
      />
    </LoadingContent>
  );
};

SeasonEventPerformance.propTypes = {
  clubID: PropTypes.string,
  onDataChange: PropTypes.func,
};

export default SeasonEventPerformance;
